import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
    ShoppingCart, 
    Package, 
    BarChart3, 
    Users, 
    CreditCard, 
    Globe,
    Zap,
    CheckCircle2,
    ArrowRight,
    Star,
    Shield,
    Layers,
    Clock,
    Sparkles
} from 'lucide-react';

// Logo URLs from uploaded assets
const LOGO_HORIZONTAL = "/mezgebpos-logo.png";
const LOGO_COVER = "/mezgebpos-logo.png";

const FEATURES = [
    {
        icon: ShoppingCart,
        title: "Lightning-Fast POS",
        description: "Process sales instantly with our streamlined checkout system"
    },
    {
        icon: Package,
        title: "Smart Inventory",
        description: "Real-time stock tracking with automatic reorder alerts"
    },
    {
        icon: BarChart3,
        title: "Powerful Analytics",
        description: "Deep insights into your sales, trends, and performance"
    },
    {
        icon: Users,
        title: "Team Management",
        description: "Role-based access control for your entire staff"
    },
    {
        icon: CreditCard,
        title: "Unified Payments",
        description: "Accept all payment methods in one seamless flow"
    },
    {
        icon: Globe,
        title: "Cloud-Based",
        description: "Access your store data from anywhere, any device"
    }
];

const TESTIMONIALS = [
    {
        name: "Marcus T.",
        role: "Restaurant Owner",
        text: "Mezgeb POS cut my checkout time in half. The inventory tracking is incredibly precise.",
        rating: 5
    },
    {
        name: "Fatima A.",
        role: "Boutique Manager",
        text: "Finally, a POS system that doesn't require an IT degree to use. Clean and powerful.",
        rating: 5
    },
    {
        name: "David K.",
        role: "Multi-Store Director",
        text: "Managing 5 locations has never been easier. The analytics alone are worth it.",
        rating: 5
    }
];

const PLANS = [
    { name: "Starter", price: 29, period: "month" },
    { name: "Professional", price: 79, period: "month", popular: true },
    { name: "Enterprise", price: 199, period: "month" }
];

export default function LandingPage() {
    const navigate = useNavigate();

    return (
        <div className="min-h-screen bg-slate-950" data-testid="landing-page">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/95 backdrop-blur-md border-b border-slate-800">
                <div className="container mx-auto px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <img src={LOGO_HORIZONTAL} alt="Mezgeb POS Inventory Software" className="h-32 w-auto" />
                    </div>
                    <div className="hidden md:flex items-center gap-8">
                        <a href="#features" className="text-slate-400 hover:text-cyan-400 transition-colors text-sm font-medium tracking-wide">FEATURES</a>
                        <a href="#pricing" className="text-slate-400 hover:text-cyan-400 transition-colors text-sm font-medium tracking-wide">PRICING</a>
                        <a href="#testimonials" className="text-slate-400 hover:text-cyan-400 transition-colors text-sm font-medium tracking-wide">REVIEWS</a>
                    </div>
                    <div className="flex items-center gap-3">
                        <Button 
                            variant="ghost" 
                            onClick={() => navigate('/login')}
                            className="text-slate-400 hover:text-white hover:bg-slate-800"
                            data-testid="nav-login-btn"
                        >
                            Sign In
                        </Button>
                        <Button 
                            onClick={() => navigate('/pricing')}
                            className="bg-cyan-500 hover:bg-cyan-600 text-slate-950 font-semibold"
                            data-testid="nav-get-started-btn"
                        >
                            Get Started
                        </Button>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="pt-28 pb-20 px-4 relative overflow-hidden">
                {/* Background gradient */}
                <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-slate-950 to-slate-950" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-cyan-900/20 via-transparent to-transparent" />
                
                <div className="container mx-auto text-center relative z-10">
                    <Badge className="mb-6 bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 px-4 py-1.5 text-xs tracking-widest">
                        POINT OF SALE SOFTWARE
                    </Badge>
                    
                    <h1 className="text-4xl md:text-6xl font-bold mb-4 text-white tracking-tight">
                        The Modern POS System
                        <br />
                        <span className="text-cyan-400">for Growing Businesses</span>
                    </h1>
                    
                    <p className="text-lg md:text-xl text-slate-400 mb-10 max-w-2xl mx-auto leading-relaxed">
                        Streamline your restaurant, retail store, or café with our powerful point-of-sale system. 
                        Manage inventory, process payments, and grow your business.
                    </p>
                    
                    <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                        <Button 
                            size="lg"
                            onClick={() => navigate('/pricing')}
                            className="bg-cyan-500 hover:bg-cyan-600 text-slate-950 text-lg px-10 py-6 font-semibold tracking-wide"
                            data-testid="hero-get-started-btn"
                        >
                            <Sparkles className="mr-2 h-5 w-5" />
                            START FREE TRIAL
                        </Button>
                        <Button 
                            size="lg"
                            variant="outline"
                            onClick={() => navigate('/login')}
                            className="text-lg px-10 py-6 border-slate-700 text-white hover:bg-slate-800 hover:border-cyan-500"
                        >
                            VIEW DEMO
                        </Button>
                    </div>
                    
                    <div className="flex items-center justify-center gap-8 text-sm text-slate-500 flex-wrap">
                        <div className="flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4 text-cyan-500" />
                            <span>14-day free trial</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4 text-cyan-500" />
                            <span>No credit card required</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4 text-cyan-500" />
                            <span>Cancel anytime</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats Bar */}
            <section className="py-8 px-4 bg-slate-900 border-y border-slate-800">
                <div className="container mx-auto">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                        {[
                            { value: "500+", label: "Active Stores" },
                            { value: "1M+", label: "Transactions" },
                            { value: "99.9%", label: "Uptime" },
                            { value: "24/7", label: "Support" }
                        ].map((stat, idx) => (
                            <div key={idx}>
                                <div className="text-3xl md:text-4xl font-bold text-cyan-400">{stat.value}</div>
                                <div className="text-slate-500 text-sm tracking-wide">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section id="features" className="py-20 px-4 bg-slate-950">
                <div className="container mx-auto">
                    <div className="text-center mb-16">
                        <Badge className="mb-4 bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 tracking-widest text-xs">
                            FEATURES
                        </Badge>
                        <h2 className="text-4xl font-bold mb-4 text-white">Built for Performance</h2>
                        <p className="text-slate-400 max-w-2xl mx-auto">
                            Enterprise-grade tools designed for businesses that demand reliability
                        </p>
                    </div>
                    
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {FEATURES.map((feature, idx) => (
                            <Card key={idx} className="bg-slate-900 border-slate-800 hover:border-cyan-500/50 transition-all duration-300 group">
                                <CardContent className="p-6">
                                    <div className="w-12 h-12 bg-slate-800 border border-slate-700 rounded-lg flex items-center justify-center mb-4 group-hover:bg-cyan-500/20 group-hover:border-cyan-500/30 transition-colors">
                                        <feature.icon className="h-6 w-6 text-cyan-400" />
                                    </div>
                                    <h3 className="text-xl font-semibold mb-2 text-white">{feature.title}</h3>
                                    <p className="text-slate-400">{feature.description}</p>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* Why Choose Section */}
            <section className="py-20 px-4 bg-slate-900">
                <div className="container mx-auto">
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <div>
                            <Badge className="mb-4 bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 tracking-widest text-xs">
                                WHY MEZGEB
                            </Badge>
                            <h2 className="text-4xl font-bold mb-6 text-white">
                                One Platform.
                                <br />
                                <span className="text-cyan-400">Complete Control.</span>
                            </h2>
                            <p className="text-slate-400 mb-8 leading-relaxed">
                                Mezgeb POS brings together everything you need to run your retail business. 
                                From point-of-sale to inventory management to analytics — all in one powerful, 
                                easy-to-use platform.
                            </p>
                            <div className="space-y-4">
                                {[
                                    { icon: Layers, text: "Multi-store management from one dashboard" },
                                    { icon: Shield, text: "Bank-level security for all transactions" },
                                    { icon: Clock, text: "Real-time sync across all devices" },
                                    { icon: Zap, text: "Lightning-fast checkout processing" }
                                ].map((item, idx) => (
                                    <div key={idx} className="flex items-center gap-3">
                                        <div className="w-8 h-8 bg-cyan-500/10 rounded flex items-center justify-center">
                                            <item.icon className="h-4 w-4 text-cyan-400" />
                                        </div>
                                        <span className="text-slate-300">{item.text}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="relative flex justify-center">
                            <div className="bg-slate-800 rounded-xl p-8 border border-slate-700">
                                <img src={LOGO_COVER} alt="Mezgeb POS" className="w-64 h-auto" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Pricing Preview */}
            <section id="pricing" className="py-20 px-4 bg-slate-950">
                <div className="container mx-auto text-center">
                    <Badge className="mb-4 bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 tracking-widest text-xs">
                        PRICING
                    </Badge>
                    <h2 className="text-4xl font-bold mb-4 text-white">Simple, Transparent Pricing</h2>
                    <p className="text-slate-400 mb-12 max-w-2xl mx-auto">
                        Choose the plan that fits your business. No hidden fees. Cancel anytime.
                    </p>
                    
                    <div className="flex flex-wrap justify-center gap-6 mb-10">
                        {PLANS.map((plan, idx) => (
                            <Card key={idx} className={`w-72 bg-slate-900 border-slate-800 transition-all duration-300 hover:border-cyan-500/50 ${plan.popular ? 'border-cyan-500 scale-105' : ''}`}>
                                <CardContent className="p-6 text-center">
                                    {plan.popular && (
                                        <Badge className="mb-3 bg-cyan-500 text-slate-950 font-semibold">MOST POPULAR</Badge>
                                    )}
                                    <h3 className="text-xl font-semibold mb-2 text-white">{plan.name}</h3>
                                    <div className="mb-4">
                                        <span className="text-4xl font-bold text-white">${plan.price}</span>
                                        <span className="text-slate-500">/{plan.period}</span>
                                    </div>
                                    <Button 
                                        className={`w-full ${plan.popular ? 'bg-cyan-500 hover:bg-cyan-600 text-slate-950' : 'bg-slate-800 hover:bg-slate-700 text-white border border-slate-700'}`}
                                        onClick={() => navigate('/pricing')}
                                    >
                                        Get Started
                                    </Button>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                    
                    <Button 
                        size="lg"
                        onClick={() => navigate('/pricing')}
                        className="bg-transparent border border-cyan-500 text-cyan-400 hover:bg-cyan-500 hover:text-slate-950"
                        data-testid="pricing-view-plans-btn"
                    >
                        View All Plans & Features
                        <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                </div>
            </section>

            {/* Testimonials */}
            <section id="testimonials" className="py-20 px-4 bg-slate-900">
                <div className="container mx-auto">
                    <div className="text-center mb-16">
                        <Badge className="mb-4 bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 tracking-widest text-xs">
                            TESTIMONIALS
                        </Badge>
                        <h2 className="text-4xl font-bold mb-4 text-white">Trusted by Businesses</h2>
                        <p className="text-slate-400">See what business owners are saying</p>
                    </div>
                    
                    <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                        {TESTIMONIALS.map((testimonial, idx) => (
                            <Card key={idx} className="bg-slate-800 border-slate-700">
                                <CardContent className="p-6">
                                    <div className="flex gap-1 mb-4">
                                        {[...Array(testimonial.rating)].map((_, i) => (
                                            <Star key={i} className="h-4 w-4 fill-cyan-400 text-cyan-400" />
                                        ))}
                                    </div>
                                    <p className="text-slate-300 mb-4 italic">"{testimonial.text}"</p>
                                    <div>
                                        <div className="font-semibold text-white">{testimonial.name}</div>
                                        <div className="text-sm text-slate-500">{testimonial.role}</div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 px-4 bg-slate-950">
                <div className="container mx-auto">
                    <Card className="bg-gradient-to-r from-slate-900 to-slate-800 border-slate-700 overflow-hidden">
                        <CardContent className="p-12 md:p-16 text-center relative">
                            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-cyan-900/20 via-transparent to-transparent" />
                            <div className="relative z-10">
                                <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
                                    Ready to Get Started?
                                </h2>
                                <p className="text-xl text-slate-400 mb-8 max-w-2xl mx-auto">
                                    Join hundreds of businesses using Mezgeb POS to streamline their operations.
                                </p>
                                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                    <Button 
                                        size="lg"
                                        onClick={() => navigate('/pricing')}
                                        className="bg-cyan-500 hover:bg-cyan-600 text-slate-950 text-lg px-10 font-semibold"
                                        data-testid="cta-get-started-btn"
                                    >
                                        <Sparkles className="mr-2 h-5 w-5" />
                                        Start Free Trial
                                    </Button>
                                    <Button 
                                        size="lg"
                                        variant="outline"
                                        onClick={() => navigate('/login')}
                                        className="border-slate-600 text-white hover:bg-slate-800 text-lg px-10"
                                    >
                                        Sign In
                                    </Button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 px-4 bg-slate-900 border-t border-slate-800">
                <div className="container mx-auto">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                        <div className="flex items-center gap-3">
                            <img src={LOGO_HORIZONTAL} alt="Mezgeb POS" className="h-28 w-auto" />
                        </div>
                        <div className="flex items-center gap-6 text-sm text-slate-500">
                            <a href="#features" className="hover:text-cyan-400 transition-colors">Features</a>
                            <a href="#pricing" className="hover:text-cyan-400 transition-colors">Pricing</a>
                            <a href="#testimonials" className="hover:text-cyan-400 transition-colors">Reviews</a>
                            <a href="/login" className="hover:text-cyan-400 transition-colors">Sign In</a>
                        </div>
                        <div className="text-sm text-slate-600">
                            © 2026 Mezgeb POS. All rights reserved.
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
